// WindowPage.jsx
// The public, read-only view of a user's plant window, mirroring the iOS
// app's PlantCollectionView (rooms, normalized plant positions, name pills).

// Packages, Dependencies, and Shared Libraries
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { openInNewTab, isUserPublic } from "../sharedFunctions";
import {
  getUserFromDatabase,
  getPlantsByOwner,
  getSpeciesFromDatabase,
} from "../DatabaseOperations";

// React Components
import BioPageHeader from "../BioPage/Header/BioPageHeader";
import Loading from "../MiscPages/Loading";
import Error from "../MiscPages/Error";
import PlantImage from "../Sprite/PlantImage";
import ShareBar from "../SharedComponents/ShareBar";
import WindowScene, {
  DEFAULT_ROOM,
  computeWindowPlacement,
} from "./WindowScene";

// Constants
import * as constants from "../constants";

/*******************************************************************************
 * @func WindowPage (React Component)
 * The public page for a user's window at /window/:userId. Fetches the user,
 * their living plants, and the species needed to draw them, then renders the
 * window scene per room with a room switcher, plus the rest of the collection.
 *
 * @param None
 * @return JSX Element
 *
 * @notes Read-only. Matches the iOS app's eligibility rules: living plants
 * with a valid windowPosition, capped at 10 per room, others shown in a grid
 *
 ******************************************************************************/
const WindowPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  // Hooks
  const [user, setUser] = useState(null);
  const [plants, setPlants] = useState(null);
  const [speciesMap, setSpeciesMap] = useState(null);
  const [error, setError] = useState("");
  const [roomIndex, setRoomIndex] = useState(0);

  // Fetch the user and their plants, then every unique species we need
  useEffect(() => {
    if (!userId) {
      setError("No user was specified!");
      return;
    }
    Promise.all([getUserFromDatabase(userId), getPlantsByOwner(userId)]).then(
      ([userDoc, plantDocs]) => {
        // Only public profiles get a window page
        if (!isUserPublic(userDoc)) {
          setError("This gardener keeps their window private. 🤫");
          return;
        }
        setUser(userDoc);
        setPlants(plantDocs);
        const uniqueSpecies = [...new Set(plantDocs.map((p) => p.species))];
        Promise.allSettled(
          uniqueSpecies.map((speciesId) =>
            getSpeciesFromDatabase(speciesId).then((data) => [speciesId, data])
          )
        ).then((results) => {
          const map = {};
          results.forEach((result) => {
            if (result.status === "fulfilled") {
              const [speciesId, data] = result.value;
              map[speciesId] = data;
            }
          });
          setSpeciesMap(map);
        });
      },
      () => {
        setError("We couldn't find that gardener!");
      }
    );
  }, [userId]);

  // Browser tab title follows the gardener being viewed
  useEffect(() => {
    if (user?.name) {
      document.title = `${user.name}'s window | Leaflings`;
    }
    return () => {
      document.title = "Leaflings — Your Plants, Brought to Life";
    };
  }, [user]);

  const rooms =
    Array.isArray(user?.rooms) && user.rooms.length > 0
      ? user.rooms
      : [DEFAULT_ROOM];
  const currentRoom = rooms[Math.min(roomIndex, rooms.length - 1)];

  const { placedPlants, otherPlants } = useMemo(
    () => computeWindowPlacement(plants, speciesMap, currentRoom, rooms),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plants, speciesMap, currentRoom]
  );

  const hasLoaded = (user && plants && speciesMap) || error !== "";

  if (!hasLoaded) {
    return (
      <React.Fragment>
        <BioPageHeader />
        <Loading />
      </React.Fragment>
    );
  }

  if (error !== "") {
    return (
      <React.Fragment>
        <BioPageHeader />
        <Error error={error} />
      </React.Fragment>
    );
  }

  // A window is only worth showing when there's at least one drawable plant
  if (placedPlants.length === 0 && otherPlants.length === 0) {
    return (
      <React.Fragment>
        <BioPageHeader />
        <Error error={"This window doesn't have anything on display yet."} />
      </React.Fragment>
    );
  }

  const gardenerName = user.name || "A Leaflings gardener";

  return (
    <React.Fragment>
      <BioPageHeader />
      <div className="mx-auto max-w-[600px] px-3 pb-16 font-global text-leaf-brown">
        {/* Gardener profile strip */}
        <div className="pl-2 pt-3 text-leaf-green-dark text-[21px]">
          <span
            className="font-header -rotate-1 inline-block"
            style={{ textShadow: "0 1px 0 rgba(255,255,255,0.7)" }}
          >
            Welcome to...
          </span>
        </div>
        <div className="flex items-end justify-between px-2 pb-3 flex-wrap gap-2">
          <h1 className="text-3xl font-extrabold text-leaf-green-dark leading-tight">
            {gardenerName}'s window
          </h1>
          <div className="flex gap-2 text-xs font-semibold text-leaf-brown-soft pb-1">
            {Number.isFinite(user.level) && (
              <span className="bg-white rounded-full px-3 py-1 shadow-sm border border-leaf-brown/10">
                Level {user.level}
              </span>
            )}
            <span className="bg-white rounded-full px-3 py-1 shadow-sm border border-leaf-brown/10">
              {plants.length} {plants.length === 1 ? "plant" : "plants"}
            </span>
          </div>
        </div>

        {/* Room switcher (only when the user has multiple rooms) */}
        {rooms.length > 1 && (
          <div className="flex items-center gap-1.5 bg-leaf-green-soft rounded-full p-1 mb-3 mx-auto max-w-[360px] text-sm border border-leaf-green/25 shadow-sm">
            {rooms.map((room, index) => (
              <button
                key={room.id || index}
                className={`flex-1 rounded-full px-3 py-1 font-semibold transition-all ${
                  index === roomIndex
                    ? "bg-leaf-green-dark text-white shadow-sm"
                    : "text-leaf-green-deep hover:bg-white/60"
                }`}
                onClick={() => setRoomIndex(index)}
              >
                {room.name || `Room ${index + 1}`}
              </button>
            ))}
          </div>
        )}

        {/* The window scene */}
        <div className="mx-auto max-w-[440px]">
          <WindowScene
            room={currentRoom}
            plants={placedPlants}
            speciesMap={speciesMap}
          />
          <div className="flex items-center justify-between mt-3 px-1">
            <p className="text-xs text-leaf-brown-soft">
              Tap a plant to say hello 👋
            </p>
            <ShareBar
              title={`${gardenerName}'s window on Leaflings`}
              text={`Peek through ${gardenerName}'s window and meet their Leaflings!`}
              url={`https://reallifeplants.com/window/${userId}`}
              label="Share this window"
            />
          </div>
        </div>

        {/* The rest of the collection */}
        {otherPlants.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-extrabold text-leaf-green-dark px-2 mb-2">
              Also growing at {gardenerName}'s
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {otherPlants.map((plant) => (
                <div
                  key={plant.id}
                  className="bg-global-accent rounded-2xl shadow-card border border-leaf-brown/10 p-2 pt-3 text-center cursor-pointer hover:-translate-y-0.5 hover:bg-leaf-cream transition-all"
                  onClick={() => navigate("/plant/" + plant.id)}
                >
                  <PlantImage
                    plant={plant}
                    species={speciesMap[plant.species]}
                    onLoadCallback={() => {}}
                    width="100%"
                  />
                  <div className="text-xs font-bold mt-1 truncate">
                    {(plant.name && plant.name.trim()) ||
                      speciesMap[plant.species]?.commonName ||
                      "Leafling"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* App CTA */}
        <div className="mt-10 bg-leaf-green-dark text-white rounded-3xl shadow-lift p-6 text-center">
          <div className="font-header text-2xl mb-2 pb-1">
            Want a window like this?
          </div>
          <p className="text-white/90 text-sm mb-4">
            Grow your own Leaflings, arrange them in your window, and visit
            your friends' windows in the app.
          </p>
          <button
            className="bg-white text-leaf-green-dark font-bold rounded-full px-6 py-2.5 shadow-card hover:bg-leaf-cream transition-colors"
            onClick={() => openInNewTab(constants.APP_STORE_URL)}
          >
            Get Leaflings for iPhone
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}; // End WindowPage

export default WindowPage;
